import useRedirect from "@/hooks/useRedirect";
import { productAddedToCart } from "@/lib/mixpanels/mixpanel";
import { LIBRARIES_INDEX_PAGE, SHOP_CART_PAGE } from "@/settings/constant";
import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useCallback } from "react";
import { getCurrentCartQuery, postBooksCartItem } from "src/api/booksCart";
import { isBooksProduct } from "src/api/booksProducts";
import useHandoutProductStore from "src/store/relatedProduct";
import { usePurchaseStore } from "../purchaseStore";
export const useLegacyAddCart = (param)=>{
    let { isLoggedIn , product  } = param;
    const { redirectToLogin  } = useRedirect();
    const { openModal , closeModal  } = usePurchaseStore((state)=>({
            openModal: state.openModal,
            closeModal: state.closeModal
        }));
    const { selectedPurchaseOption  } = useHandoutProductStore((state)=>({
            selectedPurchaseOption: state.selectedPurchaseOption
        }));
    const { refetch  } = getCurrentCartQuery({
        enabled: isLoggedIn,
        params: {
            cartType: "separate"
        }
    });
    const { mutate: addItemToCart  } = useMutation({
        mutationFn: postBooksCartItem,
        onSuccess: async (data)=>{
            const { hasAlreadyOwned , purchaseProductsInfo  } = useHandoutProductStore.getState();
            let title = "장바구니에 상품 1개를 담았어요.";
            let content = "이미 장바구니에 담겨있거나 이용 중인 상품은 제외되어 담겨요.";
            if (purchaseProductsInfo.selectedRelatedProducts.length > 0) {
                let count = purchaseProductsInfo.selectedRelatedProducts.length + 1;
                if (hasAlreadyOwned) {
                    count = purchaseProductsInfo.selectedRelatedProducts.length;
                }
                title = "장바구니에 상품 ".concat(count, "개를 담았어요.");
            }
            openModal({
                title,
                content,
                severity: "success",
                suffix: {
                    variant: "solid",
                    label: "장바구니 이동",
                    handleClick: ()=>{
                        window.location.href = SHOP_CART_PAGE;
                    }
                },
                prefix: {
                    variant: "soft",
                    label: "닫기",
                    handleClick: ()=>{
                        closeModal();
                    }
                }
            });
            //mixpanel
            productAddedToCart({
                ...product,
                id: product.id,
                source_type: isBooksProduct(product) ? product.provider : product.source_type,
                source: isBooksProduct(product) ? null : product.source,
                relativeProducts: [
                    ...purchaseProductsInfo.selectedRelatedProducts.map((product)=>product.id)
                ],
                purchaseOption: selectedPurchaseOption
            });
            refetch();
        },
        onError: (error)=>{
            ;
            if (isAxiosError(error)) {
                const { response: { status , data  }  } = error;
                //여기서 이제 에러처리
                const errorMessage = data.message;
                ;
                if (status === 409 && errorMessage.includes("Found already has owned products")) {
                    //current product인 경우
                    openModal({
                        title: "이미 이용 중이에요.",
                        content: "해당 자료는 라이브러리에서 확인할 수 있어요.",
                        suffix: {
                            variant: "solid",
                            label: "자료 보관함 이동",
                            handleClick: ()=>{
                                window.location.href = LIBRARIES_INDEX_PAGE;
                            }
                        }
                    });
                }
                if (status === 409 && errorMessage === "Already added") {
                    openModal({
                        title: "이미 담은 상품이에요.",
                        content: "상품 구매는 장바구니를 통해서 가능해요.",
                        severity: "caution",
                        suffix: {
                            variant: "solid",
                            label: "장바구니 이동",
                            handleClick: ()=>{
                                window.location.href = SHOP_CART_PAGE;
                            }
                        }
                    });
                } else if (status === 409 && errorMessage === "Duplicate license") {
                    openModal({
                        title: "이미 이용 중인 자료에요.",
                        content: "해당 자료는 라이브러리에서 확인할 수 있어요.",
                        suffix: {
                            variant: "solid",
                            label: "자료 보관함 이동",
                            handleClick: ()=>{
                                window.location.href = LIBRARIES_INDEX_PAGE;
                            }
                        }
                    });
                }
            }
        }
    });
    const handleAddCartClick = useCallback(()=>{
        const { purchaseProductsInfo , currentProduct , closePassSectionModal , hasAlreadyOwned  } = useHandoutProductStore.getState();
        closePassSectionModal();
        if (!isLoggedIn) {
            redirectToLogin();
            return;
        }
        const productType = isBooksProduct(product) ? product.productType : product.source_type;
        if (productType === "handout") {
            //연관교재 조회 및 연관교재 함께구매
            const { selectedRelatedProducts  } = purchaseProductsInfo;
            if (selectedRelatedProducts.length === 0 && hasAlreadyOwned) {
                openModal({
                    title: "이미 이용중인 자료에요.",
                    content: "해당 자료는 라이브러리에서 확인할 수 있어요.",
                    suffix: {
                        variant: "solid",
                        label: "자료 보관함 이동",
                        handleClick: ()=>{
                            window.location.href = LIBRARIES_INDEX_PAGE;
                        }
                    }
                });
                return;
            }
            const mapToCartItem = (product)=>({
                    productId: "".concat(product.id),
                    choicePurchaseOption: selectedPurchaseOption
                });
            // 선택된 관련 상품을 매핑합니다
            const relatedProductItems = selectedRelatedProducts.map(mapToCartItem);
            // hasAlreadyOwned 조건에 따라 현재 상품을 추가할지 결정합니다
            const cartItems = hasAlreadyOwned ? relatedProductItems : [
                ...relatedProductItems,
                {
                    productId: currentProduct.id.toString(),
                    choicePurchaseOption: selectedPurchaseOption
                }
            ];
            addItemToCart({
                cartType: "separate",
                cartItems
            });
            return;
        }
        addItemToCart({
            cartType: isBooksProduct(product) ? product.productType : "separate",
            cartItems: [
                {
                    productId: "".concat(product.id),
                    choicePurchaseOption: selectedPurchaseOption !== null && selectedPurchaseOption !== void 0 ? selectedPurchaseOption : "view"
                }
            ]
        });
    }, [
        isLoggedIn,
        redirectToLogin,
        product,
        addItemToCart,
        selectedPurchaseOption
    ]);
    return {
        handleAddCartClick
    };
};
